import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import LandingPage from './components/LandingPage';
import Auth from './components/Auth';
import WaitlistPage from './components/WaitlistPage';
import ResetPassword from './components/ResetPassword';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import Settings from './components/Settings';
import WatchList from './components/WatchList';
import SupportCenter from './components/SupportCenter';
import { Analytics } from "@vercel/analytics/react"

// Protected Route Component
function ProtectedRoute({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waitlistStatus, setWaitlistStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const checkWaitlistStatus = async (user) => {
    try {
      // Since the user has already passed the Auth component's waitlist check,
      // we can do a simple verification query here
      const { data: waitlistEntry, error: waitlistError } = await Promise.race([
        supabase
          .from('waitlist')
          .select('approved, user_id, email')
          .eq('email', user.email)
          .maybeSingle(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Waitlist verification timeout after 3000ms')), 3000)
        )
      ]);
      
      if (!waitlistError && waitlistEntry) {
        setWaitlistStatus(waitlistEntry);
      } else {
        // If the query fails but user got through Auth, assume they're approved
        setWaitlistStatus({ user_id: user.id, email: user.email, approved: true });
      }
      
    } catch (err) {
      // If verification fails but user got through Auth, assume they're approved
      setWaitlistStatus({ user_id: user.id, email: user.email, approved: true });
    }
  };

  useEffect(() => {
    let mounted = true;
    
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      
      setSession(session);
      
      if (session) {
        await checkWaitlistStatus(session.user);
      }
      
      if (mounted) {
        setLoading(false);
      }
    }).catch(err => {
      if (!mounted) return;
      setError(err.message);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;
      
      setSession(session);
      
      if (session) {
        await checkWaitlistStatus(session.user);
      } else {
        setWaitlistStatus(null);
      }
      
      if (mounted) {
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="status-card">
            <div className="spinner" />
            <div>
              <h3>Preparing your workspace</h3>
              <p>Checking session and waitlist access...</p>
            </div>
          </div>
          {error && (
            <div className="error-banner mt-4">
              <p>Error: {error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Not logged in - redirect to login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Logged in but not approved - redirect to waitlist
  if (!waitlistStatus?.approved) {
    return <Navigate to="/waitlist" replace />;
  }

  // Approved user - render protected content
  return children;
}

// Main App Component
function AppContent() {
  const [watchlistCoin, setWatchlistCoin] = useState(null);
  const [showPrevious, setShowPrevious] = useState(false);
  const [clearAnalysisData, setClearAnalysisData] = useState(false);
  const [session, setSession] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Get session for header
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Handle URL parameters for watchlist
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const wlCoin = urlParams.get('wl');
    const previousParam = urlParams.get('previous');
    const clearParam = urlParams.get('clear');
    
    if (wlCoin) {
      setWatchlistCoin(wlCoin);
      setShowPrevious(previousParam === 'true');
      setClearAnalysisData(false); // Don't clear when viewing watchlist item
    } else {
      // Clear watchlist state when no wl parameter is present
      setWatchlistCoin(null);
      setShowPrevious(false);
      // Set clear flag based on URL parameter (true when navigating via dashboard nav)
      setClearAnalysisData(clearParam === 'true');
    }
  }, [location.search]);

  const handleNavigation = (page) => {
    if (page === 'dashboard') {
      // Add clear parameter to indicate explicit navigation to dashboard
      navigate('/dashboard?clear=true');
    } else if (page === 'settings') {
      navigate('/settings');
    } else if (page === 'watchlist') {
      navigate('/watchlist');
    } else if (page === 'support') {
      navigate('/support');
    }
  };

  const handleViewDetails = (coin, showPreviousData = false) => {
    setWatchlistCoin(coin);
    setShowPrevious(showPreviousData);
    
    // Update URL with watchlist parameter and previous flag
    const params = new URLSearchParams();
    params.set('wl', coin);
    if (showPreviousData) params.set('previous', 'true');
    
    navigate(`/dashboard?${params.toString()}`);
  };

  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'dashboard';
    if (path === '/settings') return 'settings';
    if (path === '/watchlist') return 'watchlist';
    if (path === '/support') return 'support';
    return 'dashboard';
  };

  return (
    <div className="min-h-screen">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Auth />} />
        <Route path="/waitlist" element={<WaitlistPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <div>
              <Header 
                user={session?.user} 
                currentPage={getCurrentPage()} 
                onNavigate={handleNavigation} 
              />
              <Dashboard 
                user={session?.user} 
                watchlistCoin={watchlistCoin} 
                showPrevious={showPrevious}
                clearAnalysisData={clearAnalysisData}
                onNavigateToWatchlist={() => navigate('/watchlist')}
              />
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/settings" element={
          <ProtectedRoute>
            <div>
              <Header 
                user={session?.user} 
                currentPage={getCurrentPage()} 
                onNavigate={handleNavigation} 
              />
              <Settings user={session?.user} />
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/watchlist" element={
          <ProtectedRoute>
            <div>
              <Header 
                user={session?.user} 
                currentPage={getCurrentPage()} 
                onNavigate={handleNavigation} 
              />
              <WatchList user={session?.user} onViewDetails={handleViewDetails} />
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/support" element={
          <ProtectedRoute>
            <div>
              <Header 
                user={session?.user} 
                currentPage={getCurrentPage()} 
                onNavigate={handleNavigation} 
              />
              <SupportCenter user={session?.user} />
            </div>
          </ProtectedRoute>
        } />
        
        {/* Redirect any unknown routes to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
      <Analytics />
    </Router>
  );
}

export default App;
